if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';

        this.initFinalSaleProperty();
      }

      // The buy buttons markup is not re-rendered on variant change (product-info.js only
      // rewrites `input[name="id"]`), so the `_final_sale` line item property is toggled here
      // from the variant id allowlist rendered by Liquid.
      initFinalSaleProperty() {
        this.finalSaleInput = this.form.querySelector('[data-final-sale-property]');
        if (!this.finalSaleInput) return;

        this.finalSaleVariantIds = new Set(
          (this.dataset.finalSaleVariants || '')
            .split(',')
            .map((id) => id.trim())
            .filter(Boolean)
        );

        this.variantIdInput?.addEventListener('change', this.updateFinalSaleProperty.bind(this));
        this.updateFinalSaleProperty();
      }

      // Disabled inputs are left out of FormData, so toggling `disabled` adds/removes the property.
      updateFinalSaleProperty() {
        if (!this.finalSaleInput) return;

        const variantId = this.variantIdInput?.value;
        this.finalSaleInput.disabled = !variantId || !this.finalSaleVariantIds.has(String(variantId));
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        // Check for imprint-3d modal requirement
        const isImprint3d = this.dataset.isImprint3d === 'true';
        if (isImprint3d) {
          const imprintModal = document.querySelector('imprint-3d-modal');
          const overlay = document.querySelector('.imprint-3d-modal-overlay');
          if (imprintModal) {
            overlay.classList.add('show');
            imprintModal.show();
          }
          return; // Stop execution here, don't add to cart
        }

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure('add:paint-updated-sections', () => {
                      this.cart.renderMiniCartContents(response);
                    });
                  });
                },
                { once: true }
              );
              this.submitButton.removeAttribute('aria-disabled');
              this.submitButton.classList.remove('loading');
              this.submitButton.querySelector('span').innerHTML = window.cartStrings.itemAdded;
              const t = window.setTimeout(() => {
                quickAddModal.hide(true);
                window.clearTimeout(t);
              }, 1000);
            } else {
              CartPerformance.measure('add:paint-updated-sections', () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent('add:user-action', evt);

            if (!this.error) {
              const imprint3dModal = document.querySelector('imprint-3d-modal');
              if (imprint3dModal) imprint3dModal.hide();

              const overlay = document.querySelector('.imprint-3d-modal-overlay');
              if (overlay) overlay.classList.remove('show');
            }
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          const isImprint3d = this.dataset.isImprint3d === 'true';
          const imprintModal = document.querySelector('imprint-3d-modal');

          // Only update the text if this button is NOT inside the imprint-3d modal
          const insideImprintModal = imprintModal && imprintModal.contains(this.submitButton);

          if (!insideImprintModal) {
            if (isImprint3d) {
              this.submitButtonText.textContent = window.variantStrings.confirm_your_selections;
            } else {
              this.submitButtonText.textContent = window.variantStrings.addToCart;
            }
          }
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
